import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { ApprovalSimulator } from "@/components/landing/ApprovalSimulator";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1530521954074-e64f6810b32d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMHRyYXZlbCUyMGV4ZWN1dGl2ZSUyMHBsYW5lJTIwYWlycG9ydHxlbnwwfHx8fDE3ODk3MTQ3MDV8MA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.12 * i, duration: 0.6, ease: "easeOut" },
  }),
};

export const Hero = () => {
  return (
    <section id="top" data-testid="hero-section" className="relative noise-bg overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="Corporate traveler at airport"
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#090d16]/70 via-[#090d16]/85 to-[#090d16]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 pt-36 pb-24 lg:pt-44 lg:pb-32 grid lg:grid-cols-[1.15fr_0.85fr] gap-16 items-center">
        <div>
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            <span
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-blue-500/40 bg-blue-600/10 font-mono text-[11px] tracking-widest text-blue-300 uppercase"
            >
              Corporate Travel & Expense Management
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={1}
            data-testid="hero-headline"
            className="mt-6 font-heading font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          >
            Zero-Friction Corporate Travel &{" "}
            <span className="text-blue-500">Expense Intelligence</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={2}
            data-testid="hero-subheadline"
            className="mt-6 text-slate-400 text-base md:text-lg max-w-xl leading-relaxed"
          >
            Unify pre-travel requests, automated policy enforcement, travel desk
            booking execution, and real-time expense reconciliation in one
            enterprise system — from the first request to the final settlement.
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={3}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Link
              to="/login"
              data-testid="hero-cta-live-demo-button"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-all duration-200 active:scale-95 shadow-lg shadow-blue-600/30"
            >
              <Play className="w-4 h-4 fill-white" />
              Try Live App Demo
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
            <a
              href="#demo"
              data-testid="hero-cta-request-demo-button"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md border border-white/20 text-white font-semibold text-sm hover:bg-white/10 transition-all duration-200 active:scale-95"
            >
              Schedule Enterprise Demo
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={4}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-4"
          >
            {[
              ["$1.4B+", "Spend processed"],
              ["99.4%", "Policy compliance"],
              ["4.2x", "Faster expense audit"],
            ].map(([v, l]) => (
              <div key={l}>
                <p className="font-heading font-bold text-2xl text-white">{v}</p>
                <p className="font-mono text-[10px] tracking-widest text-slate-500 uppercase mt-1">{l}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.7, ease: "easeOut" }}
        >
          <ApprovalSimulator />
        </motion.div>
      </div>
    </section>
  );
};
